import { useState } from "react";
import PageHero from "@/components/site/PageHero";
import { Link } from "react-router-dom";
import { ArrowRight, Download, FileText, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { NAICS_CODES, PAST_PERFORMANCE, COMPANY } from "@/lib/content";
import { submitRFP } from "@/lib/api";
import FileDropzone from "@/components/site/FileDropzone";

const CAPABILITY_URL = "/assets/capability-statement.png";

const ADVANTAGES = [
  { t: "Small business agility", d: "Fast turnaround on inquiries, proposals, and staffing needs." },
  { t: "Regulated-industry background", d: "Direct experience with healthcare, nonprofit, and government workforces." },
  { t: "Compliance-first culture", d: "SCA, prevailing wage, and DCAA-aligned practices built into every engagement." },
  { t: "Proposal-ready posture", d: "Capability statement, past performance, and references available on request." },
];

export default function GovConPage() {
  const [form, setForm] = useState({
    agency_or_company: "",
    contact_name: "",
    email: "",
    phone: "",
    solicitation_number: "",
    naics_code: "",
    contract_vehicle: "",
    response_deadline: "",
    scope_summary: "",
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setProgress(0);
    try {
      await submitRFP(form, files, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      const suffix = files.length ? ` (${files.length} attachment${files.length > 1 ? "s" : ""} uploaded)` : "";
      toast.success(`RFP inquiry received${suffix}. Our team will respond within one business day.`);
      setForm({ agency_or_company: "", contact_name: "", email: "", phone: "", solicitation_number: "", naics_code: "", contract_vehicle: "", response_deadline: "", scope_summary: "" });
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setLoading(false); setProgress(0); }
  };

  return (
    <div data-testid="govcon-page">
      <PageHero
        eyebrow="Government Contracting"
        title="A proposal-ready HR and workforce services partner."
        subtitle="Federal, state, and local agencies partner with Groman Associates for administrative support, human resources services, program operations, and workforce management delivered with the compliance rigor government contracts demand."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Government Contracting" }]}
        image="https://images.unsplash.com/photo-1557160854-e1e89fdd3286?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHx3YXNoaW5ndG9uJTIwZGMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgzNzM0MTkwfDA&ixlib=rb-4.1.0&q=85"
      />

      {/* Capability Statement */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="border border-gray-200 bg-white p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-3">Capability Statement</div>
            <h2 className="font-heading text-2xl md:text-4xl font-semibold text-charcoal tracking-tight">Everything you need to evaluate {COMPANY.short} for your solicitation.</h2>
            <p className="mt-4 text-charcoal-soft leading-relaxed max-w-2xl">Company overview, differentiators, NAICS codes, core competencies, past performance, and points of contact — assembled for procurement officers, prime contractors, and evaluators.</p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <a
              href={CAPABILITY_URL}
              target="_blank"
              rel="noreferrer"
              download="Groman-Associates-Capability-Statement.png"
              data-testid="download-capability-statement"
              className="inline-flex items-center justify-center gap-2 bg-gold text-white px-6 py-4 text-sm font-medium hover:bg-gold-hover transition-colors"
            >
              <Download className="w-4 h-4" /> Download Capability Statement
            </a>
            <a href="#rfp-form" data-testid="govcon-rfp-scroll" className="inline-flex items-center justify-center gap-2 border border-charcoal text-charcoal px-6 py-4 text-sm font-medium hover:bg-charcoal hover:text-white transition-colors">
              <FileText className="w-4 h-4" /> Submit an RFP Response Inquiry
            </a>
          </div>
        </div>
      </section>

      {/* Registrations */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-16">
        <div className="overline mb-4">Registrations &amp; Codes</div>
        <div className="grid grid-cols-2 md:grid-cols-4 grid-borders">
          {[
            { k: "UEI", v: "Available on Request" },
            { k: "CAGE Code", v: "Available on Request" },
            { k: "SAM.gov", v: "Registered" },
            { k: "DUNS (Legacy)", v: "On File" },
            { k: "State of MO", v: "Registered" },
            { k: "EIN", v: "On File" },
            { k: "Small Business", v: "Yes" },
            { k: "HQ", v: "St. Louis, MO" },
          ].map((x) => (
            <div key={x.k} className="p-6 bg-white">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-charcoal-soft">{x.k}</div>
              <div className="mt-3 font-heading text-base text-charcoal">{x.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* NAICS */}
      <section className="bg-offwhite border-y border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline mb-4">NAICS Codes</div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal tracking-tight">Aligned to the work you're procuring.</h2>
            <p className="mt-4 text-charcoal-soft">Primary and secondary NAICS codes under which Groman Associates competes.</p>
          </div>
          <div className="lg:col-span-8">
            <table data-testid="naics-table" className="w-full text-left border border-gray-200 bg-white">
              <thead>
                <tr className="bg-charcoal text-white">
                  <th className="p-4 font-mono text-[10px] tracking-[0.24em] uppercase">Code</th>
                  <th className="p-4 font-mono text-[10px] tracking-[0.24em] uppercase">Description</th>
                </tr>
              </thead>
              <tbody>
                {NAICS_CODES.map((n, i) => (
                  <tr key={n.code} className={i % 2 ? "bg-offwhite" : "bg-white"}>
                    <td className="p-4 font-mono text-charcoal font-medium border-t border-gray-100">{n.code}</td>
                    <td className="p-4 text-charcoal-soft border-t border-gray-100">{n.title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="overline mb-4">Contracting Advantages</div>
        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal tracking-tight max-w-3xl">Why prime contractors and agencies choose Groman Associates.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-0 grid-borders">
          {ADVANTAGES.map((a, i) => (
            <div key={a.t} className="p-8 bg-white">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-gold" />
                <div className="font-mono text-xs text-charcoal-soft">0{i + 1}</div>
              </div>
              <div className="mt-6 font-heading text-xl font-medium text-charcoal">{a.t}</div>
              <p className="mt-3 text-sm text-charcoal-soft leading-relaxed">{a.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Past Performance */}
      <section className="bg-charcoal text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="overline text-gold mb-4">Past Performance</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight max-w-2xl">Representative engagements. Detailed references furnished on request.</h2>
          <div className="mt-12 overflow-x-auto">
            <table data-testid="past-performance-table" className="w-full text-left border border-white/15 min-w-[720px]">
              <thead>
                <tr>
                  {["Client", "Scope", "Contract Value", "Period"].map((h) => (
                    <th key={h} className="p-4 font-mono text-[10px] tracking-[0.24em] uppercase text-white/60 border-b border-white/15">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PAST_PERFORMANCE.map((p) => (
                  <tr key={p.client} className="border-b border-white/10">
                    <td className="p-4 font-heading font-medium">{p.client}</td>
                    <td className="p-4 text-white/75">{p.scope}</td>
                    <td className="p-4 text-gold font-mono">{p.value}</td>
                    <td className="p-4 text-white/60 font-mono">{p.period}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CONTACT BAND (moved above the RFP form) */}
      <section className="bg-offwhite border-t border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="overline mb-2">Prefer a Direct Conversation?</div>
            <div className="font-heading text-xl text-charcoal">Reach our contracts lead directly.</div>
          </div>
          <Link to="/contact" data-testid="govcon-contact-cta" className="inline-flex items-center gap-2 border border-charcoal px-6 py-4 text-sm font-medium hover:bg-charcoal hover:text-white transition-colors">Contact Groman Associates <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* RFP FORM — anchored at the bottom of the page */}
      <section id="rfp-form" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 border-t border-gray-200">
        <div className="lg:col-span-4">
          <div className="overline mb-4">RFP / Vendor Inquiry</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal tracking-tight leading-tight">Submit an RFP Response or Vendor Inquiry.</h2>
          <p className="mt-4 text-charcoal-soft leading-relaxed">Whether you're a contracting officer, prime contractor, or partner considering Groman Associates for a solicitation — this form routes directly to our proposals team.</p>
        </div>

        <form onSubmit={submit} data-testid="rfp-form" className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Agency / Company" name="agency_or_company" value={form.agency_or_company} onChange={onChange} required />
          <Field label="Contact Name" name="contact_name" value={form.contact_name} onChange={onChange} required />
          <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
          <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
          <Field label="Solicitation Number" name="solicitation_number" value={form.solicitation_number} onChange={onChange} />
          <Field label="NAICS Code" name="naics_code" value={form.naics_code} onChange={onChange} />
          <Field label="Contract Vehicle (GSA, IDIQ, etc.)" name="contract_vehicle" value={form.contract_vehicle} onChange={onChange} />
          <Field label="Response Deadline" name="response_deadline" type="date" value={form.response_deadline} onChange={onChange} />
          <div className="md:col-span-2">
            <label className="block text-xs font-mono uppercase tracking-[0.24em] text-charcoal-soft mb-2">Scope Summary *</label>
            <textarea
              name="scope_summary"
              required
              rows={5}
              value={form.scope_summary}
              onChange={onChange}
              data-testid="rfp-scope"
              className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
              placeholder="Briefly describe the scope, evaluation criteria, and any small-business set-aside status."
            />
          </div>

          <div className="md:col-span-2 mt-2">
            <FileDropzone value={files} onChange={setFiles} />
          </div>

          {loading && progress > 0 && progress < 100 && (
            <div className="md:col-span-2" data-testid="rfp-upload-progress">
              <div className="flex justify-between text-xs font-mono uppercase tracking-[0.24em] text-charcoal-soft mb-2">
                <span>Uploading attachments</span><span>{progress}%</span>
              </div>
              <div className="h-1 bg-gray-200 overflow-hidden">
                <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          <div className="md:col-span-2 flex items-center justify-between mt-4 flex-wrap gap-4">
            <p className="text-xs text-charcoal-soft">Submissions are logged and reviewed by our proposals team.</p>
            <button type="submit" disabled={loading} data-testid="rfp-submit" className="inline-flex items-center gap-2 bg-gold text-white px-6 py-4 text-sm font-medium hover:bg-gold-hover transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {loading ? "Submitting…" : "Submit RFP Inquiry"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-[0.24em] text-charcoal-soft mb-2">
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`rfp-${name}`}
        className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
      />
    </div>
  );
}
